import React from "react";
import { Button, Card, Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useAppApi } from "@/App";
import { DevForm, FormFieldErrorMessage, FormFieldGroup } from "@components/Forms/Fields";
import { useSubmit } from "@components/Forms/Handlers";
import { DefaultLayout } from "@components/Layouts";
import { url_reverse } from "@components/Utils";

export default function EventSettingsScreen() {
    const { t } = useTranslation();
    const { slug } = useParams();
    const apiUrl = url_reverse("api:event_items:list", { event_slug: slug });
    const { apiData, setApiData } = useAppApi(apiUrl);
    const onFormSubmit = useSubmit();
    const navigate = useNavigate();
    const landingSchemeIds = [34, 99];

    if (!apiData) return;

    const header = (
        <>
            <Link to={url_reverse("admin:event:items", { slug })}>{t("Управление ивентом")}</Link>
            <span> – </span>
            {t("Настройки ивента")}
        </>
    );
    const handleEventSettingsForm = (ev) => {
        const { name, checked } = ev.target;
        const newApiData = { ...apiData };
        newApiData.forms_data.main.data[name] = checked;
        setApiData(newApiData);
    };

    const handleEventRequestedContentChange = (ev) => {
        const { name } = ev.target;
        const newApiData = { ...apiData };
        if (newApiData.forms_data.main.data.requested_content.includes(name)) {
            newApiData.forms_data.main.data.requested_content =
                newApiData.forms_data.main.data.requested_content.filter((item) => item !== name);
        } else {
            newApiData.forms_data.main.data.requested_content.push(name);
        }
        setApiData(newApiData);
    };

    const handleDataToModerateChange = (ev) => {
        const { name } = ev.target;
        const newApiData = { ...apiData };
        if (newApiData.forms_data.main.data.data_to_moderate.includes(name)) {
            newApiData.forms_data.main.data.data_to_moderate = newApiData.forms_data.main.data.data_to_moderate.filter(
                (item) => item !== name
            );
        } else {
            newApiData.forms_data.main.data.data_to_moderate.push(name);
        }
        setApiData(newApiData);
    };

    return (
        <DefaultLayout>
            <Card>
                <Card.Header>{header}</Card.Header>
                <Card.Body>
                    <Form
                        onSubmit={(ev) =>
                            onFormSubmit(ev, { onOK: () => navigate(url_reverse("admin:event:items", { slug })) })
                        }
                    >
                        <DevForm.Field
                            {...{ apiData, setApiData }}
                            prefix="main"
                            name="date_event_start"
                            disabled={true}
                        />
                        <DevForm.Field
                            {...{ apiData, setApiData }}
                            prefix="main"
                            name="date_event_end"
                            disabled={true}
                        />
                        <DevForm.Field
                            {...{ apiData, setApiData }}
                            prefix="main"
                            name="date_release_estimated_variant"
                            disabled={true}
                        />
                        <DevForm.Field
                            {...{ apiData, setApiData }}
                            prefix="main"
                            name="link_to_event_page"
                            disabled={true}
                        />
                        {apiData.event.event_type === "promotion" && (
                            <FormFieldGroup label={t("Запрашиваемый контент для акций")}>
                                <>
                                    {apiData.forms_data.main.fields?.promotion_fields?.widget?.choices.map((choice) => (
                                        <DevForm.Checkbox
                                            key={choice.value}
                                            id={choice.value}
                                            name={choice.value}
                                            checked={true}
                                            type="switch"
                                            label={choice.label}
                                            disabled={true}
                                        />
                                    ))}
                                </>
                            </FormFieldGroup>
                        )}
                        {apiData.event.event_type === "promotion" && (
                            <FormFieldGroup label={t("Модерация полей для акций")}>
                                <>
                                    {apiData.forms_data.main.fields?.promotion_fields_to_moderate?.widget?.choices.map(
                                        (choice) => (
                                            <DevForm.Checkbox
                                                key={choice.value}
                                                id={choice.value}
                                                name={choice.value}
                                                checked={true}
                                                type="switch"
                                                label={choice.label}
                                                disabled={true}
                                            />
                                        )
                                    )}
                                </>
                            </FormFieldGroup>
                        )}
                        {!landingSchemeIds.includes(apiData.event.scheme_id) && (
                            <FormFieldGroup label={t("Запрашиваемый контент")}>
                                <>
                                    {apiData.forms_data.main.fields?.requested_content?.widget?.choices.map(
                                        (choice) => (
                                            <DevForm.Checkbox
                                                key={choice.value}
                                                id={choice.value}
                                                name={choice.value}
                                                checked={apiData?.forms_data?.main?.data?.requested_content?.includes(
                                                    choice.value
                                                )}
                                                onChange={(ev) => handleEventRequestedContentChange(ev)}
                                                type="switch"
                                                label={choice.label}
                                                disabled={apiData.is_newsletter_sent}
                                            />
                                        )
                                    )}
                                </>
                            </FormFieldGroup>
                        )}
                        {!landingSchemeIds.includes(apiData.event.scheme_id) && (
                            <FormFieldGroup label={t("Модерация запрашиваемых полей")}>
                                <>
                                    {apiData.forms_data.main.fields?.data_to_moderate?.widget?.choices.map((choice) => (
                                        <DevForm.Checkbox
                                            key={choice.value}
                                            id={choice.value}
                                            name={choice.value}
                                            checked={apiData?.forms_data?.main?.data?.data_to_moderate?.includes(
                                                choice.value
                                            )}
                                            onChange={(ev) => handleDataToModerateChange(ev)}
                                            type="switch"
                                            label={choice.label}
                                            disabled={apiData.is_newsletter_sent}
                                        />
                                    ))}
                                </>
                                <FormFieldErrorMessage
                                    error={apiData.forms_data.main.errors.data_to_moderate}
                                    custom={true}
                                />
                            </FormFieldGroup>
                        )}
                        <FormFieldGroup label={t("Закрытие ивента")}>
                            <DevForm.Checkbox
                                label={apiData?.forms_data?.main?.fields?.is_application_collection_closed?.label}
                                checked={apiData?.forms_data?.main?.data?.is_application_collection_closed}
                                type="switch"
                                id="is_application_collection_closed"
                                name="is_application_collection_closed"
                                onChange={(ev) => handleEventSettingsForm(ev)}
                            />
                            <DevForm.Checkbox
                                label={apiData?.forms_data?.main?.fields?.is_event_over?.label}
                                checked={apiData?.forms_data?.main?.data?.is_event_over}
                                type="switch"
                                id="is_event_over"
                                name="is_event_over"
                                disabled={apiData.event.is_event_over}
                                onChange={(ev) => handleEventSettingsForm(ev)}
                            />
                        </FormFieldGroup>
                        <DevForm.Field prefix={"main"} name={"application_collection_close_time"} />
                        <DevForm.Field prefix={"main"} name={"event_over_time"} />
                        <Button variant="primary" type="submit" name="action" value="set_event_settings">
                            {t("Сохранить")}
                        </Button>
                    </Form>
                </Card.Body>
            </Card>
        </DefaultLayout>
    );
}
